import { Link } from '@tanstack/react-router';
import type { Build } from '@/api/schemas';
import { formatDuration, shortSha, timeAgo } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useSeesProviders, useWording } from '@/lib/useAudience';
import { Icon } from '@/components/Icon';
import { EnvPill, PlatformIcon, Tag } from '@/components/badges';
import { Card } from '@/components/ui/card';
import { buildDuration, requesterOf } from './buildHelpers';
import { BuildStatusPill } from './BuildStatusPill';
import { buildNumberPrefix } from './buildsListModel';

/** Phone card (mobile mockup 02): ref + status on the first line, meta below, commit chip at the bottom. */
export function BuildCard({ build: b, index, now, projectSlug }: { build: Build; index: number; now: number; projectSlug: string }) {
  const req = requesterOf(b);
  const duration = buildDuration(b, now);
  return (
    <li className="enter" style={stagger(index)}>
      <Link to="/projects/$slug/builds/$buildId" params={{ slug: projectSlug, buildId: b.id }} data-testid={`build-row-${b.ref}`} data-status={b.status} className="block rounded-lg">
        <Card className={cn('lift gap-2.5 bg-secondary/40 p-4 shadow-none', b.environment.kind === 'production' && 'border-l-2 border-l-env-production')}>
          <CardTitleRow b={b} />
          <div className="flex items-center gap-2 text-xs text-subtle">
            <OperatorAccount name={b.providerAccount.name} />
            <span>{req.label}</span>
          </div>
          <CardPlatformRow b={b} />
          {b.error ? <CardError error={b.error} /> : null}
          <CardFooter b={b} duration={duration} now={now} />
        </Card>
      </Link>
    </li>
  );
}

function CardTitleRow({ b }: { b: Build }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex min-w-0 flex-1 font-mono text-[15px] font-medium text-foreground">
        <span className="truncate">{b.app.slug}</span>
        <span className="shrink-0 text-subtle">#{b.number}</span>
      </span>
      <BuildStatusPill status={b.status} />
    </div>
  );
}

function CardPlatformRow({ b }: { b: Build }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <PlatformIcon platform={b.platform} />
      <EnvPill env={b.environment} />
      <span className="tabular ml-auto font-mono text-xs text-muted-foreground">
        {b.versionName ?? '—'}
        {b.buildNumber ? <span className="text-subtle"> ({buildNumberPrefix(b.platform)} {b.buildNumber})</span> : null}
      </span>
    </div>
  );
}

/** The provider account the build runs on, for platform operators only (SPEC decision 23). */
function OperatorAccount({ name }: { name: string }) {
  if (!useSeesProviders()) return null;
  return (
    <>
      <span>{name}</span>
      <span aria-hidden>·</span>
    </>
  );
}

function CardError({ error }: { error: NonNullable<Build['error']> }) {
  const say = useWording();
  return (
    <span className="flex items-start gap-1.5 rounded-md border border-failed/30 bg-failed/10 px-2 py-1.5 font-mono text-[11px] text-failed">
      <Icon name="error" size={14} className="mt-px" />
      <span className="min-w-0 break-words">{say(error.message)}</span>
    </span>
  );
}

function CardFooter({ b, duration, now }: { b: Build; duration: number | null; now: number }) {
  return (
    <div className="flex items-center gap-2 border-t pt-2.5 text-xs">
      <Tag className="text-foreground">{shortSha(b.git.commitSha) || '—'}</Tag>
      <span className="min-w-0 flex-1 truncate font-mono text-muted-foreground">{b.git.branch}</span>
      <span className="tabular shrink-0 font-mono text-subtle">
        {formatDuration(duration)} · {timeAgo(b.createdAt, now)}
      </span>
    </div>
  );
}
